import React, { useRef, useState } from "react";
import styles from "./formInput.module.css";
import classNames from "classnames";

const FormInput = ({
  label,
  variant = "primary",
  size = "medium",
  width = "fullWidth",
  className,
  inputClassName,
  error,
  leftIcon,
  rightIcon,
  showToggle = false,
  showPassword = false,
  togglePassword,
  type = "text",
  value,
  onChange,
  accept,
  placeholder,
  decimalPlaces,
  ...props
}) => {
  const inputType = showToggle ? (showPassword ? "text" : "password") : type;

  const fileRef = useRef(null);
  const [fileName, setFileName] = useState("");

  const handleFileClick = () => fileRef.current.click();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFileName(file ? file.name : "");
    if (onChange) onChange(e);
  };

  // Handle numeric input with max decimal places
  const handleNumericChange = (e) => {
    const inputValue = e.target.value;
    
    if (decimalPlaces !== undefined) {
      // Allow empty input or negative sign
      if (inputValue === "" || inputValue === "-") {
        if (onChange) onChange(e);
        return;
      }

      // Only allow numbers and one decimal point
      const validPattern = new RegExp(`^\\d*\\.?\\d{0,${decimalPlaces}}$`);
      if (!validPattern.test(inputValue)) {
        return; // Reject invalid input
      }

      // Split into integer and decimal parts
      const parts = inputValue.split(".");
      
      // If there's a decimal part, limit it
      if (parts.length > 1) {
        const decimalPart = parts[1];
        if (decimalPart.length > decimalPlaces) {
          // Truncate to max decimal places
          const truncatedValue = `${parts[0]}.${decimalPart.slice(0, decimalPlaces)}`;
          // Create a synthetic event
          const syntheticEvent = {
            ...e,
            target: {
              ...e.target,
              value: truncatedValue,
            },
          };
          if (onChange) onChange(syntheticEvent);
          return;
        }
      }
    }
    
    if (onChange) onChange(e);
  };

  return (
    <div className={classNames(styles.formGroup, styles[width], className)}>
      {label && <label className={styles.label}>{label}</label>}

      <div className={styles.wrapper}>
        {/* NORMAL INPUTS (text, email, password, etc.) */}
        {type !== "file" && (
          <>
            {leftIcon && (
              <span className={styles.leftIcon}>
                <i className={`bi ${leftIcon}`} />
              </span>
            )}

            <input
              type={inputType}
              className={classNames(
                styles.input,
                styles[variant],
                styles[size],
                inputClassName,
                {
                  [styles.withLeftIcon]: leftIcon,
                  [styles.withRightIcon]: rightIcon || showToggle,
                  [styles.errorInput]: error,
                }
              )}
              placeholder={placeholder}
              value={value || ""}
              onChange={decimalPlaces !== undefined ? handleNumericChange : onChange}
              {...props}
            />

            {rightIcon && !showToggle && (
              <span className={styles.rightIcon}>
                <i className={`bi ${rightIcon}`} />
              </span>
            )}

            {showToggle && (
              <span className={styles.rightIcon} onClick={togglePassword}>
                <i
                  className={`bi ${showPassword ? "bi-eye" : "bi-eye-slash"}`}
                />
              </span>
            )}
          </>
        )}

        {/* FILE INPUT */}
        {type === "file" && (
          <div className={styles.fileContainer}>
            <input
              type="text"
              readOnly
              value={fileName}
              placeholder="No file chosen"
              className={classNames(
                styles.input,
                styles.fileInput,
                styles[size],
                inputClassName,
                { [styles.errorInput]: error }
              )}
            />

            <button
              type="button"
              className={styles.fileButton}
              onClick={handleFileClick}
            >
              Choose File
            </button>

            <input
              ref={fileRef}
              type="file"
              accept={accept}
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </div>
        )}
      </div>

      {error && <div className={styles.errorText}>{error}</div>}
    </div>
  );
};

export default FormInput;
